'use client';

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, KeyRound, Loader2, Lock, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PageHeader } from '@/components/layout/page-header';
import { Skeleton } from '@/components/ui/skeleton';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { apiKeysApi } from '@/lib/api/api-keys';
import { apiErrorMessage } from '@/lib/api/errors';
import type { ApiKeyResponse } from '@/lib/api/types';
import {
  API_DOCS_URL,
  MAX_ACTIVE_API_KEYS,
  PUBLIC_API_BASE_URL,
  accessForScopes,
  activeKeyCount,
  apiKeyStatus,
  canCreateAnotherKey,
  isForbiddenError,
} from '@/lib/api-keys';
import { usePermissions } from '@/lib/hooks/use-permissions';
import { useSort } from '@/lib/hooks/use-sort';

import { CreateApiKeyDialog } from './create-api-key-dialog';

function formatDate(value: string | null, empty: string): string {
  if (!value) return empty;
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(value));
}

function formatDateTime(value: string | null, empty: string): string {
  if (!value) return empty;
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(value),
  );
}

function KeysHeader({ badge, actions }: { badge?: React.ReactNode; actions?: React.ReactNode }) {
  return (
    <PageHeader
      title="API Keys"
      description="Let scripts, servers and no-code tools use the rii.link public API."
      badge={badge}
      actions={actions}
    />
  );
}

function UsageCard() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Using a key</CardTitle>
        <CardDescription>
          Send the key as a Bearer token with every request to the public API.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <dl className="grid gap-3 rounded-lg bg-muted p-4 text-sm sm:grid-cols-[8rem_minmax(0,1fr)]">
          <dt className="text-muted-foreground">Base URL</dt>
          <dd>
            <code className="break-all font-mono">{PUBLIC_API_BASE_URL}</code>
          </dd>
          <dt className="text-muted-foreground">Header</dt>
          <dd>
            <code className="break-all font-mono">Authorization: Bearer rii_live_…</code>
          </dd>
        </dl>
        <p className="text-sm text-muted-foreground">
          Live keys start with <code className="font-mono">rii_live_</code>, test keys with{' '}
          <code className="font-mono">rii_test_</code>. Treat a key like a password: keep it on a
          server, never in a browser or a public repository.
        </p>
        <Button asChild variant="outline">
          <a href={API_DOCS_URL} target="_blank" rel="noopener noreferrer">
            Read the API docs
            <ExternalLink aria-hidden="true" />
          </a>
        </Button>
      </CardContent>
    </Card>
  );
}

function StatusBadge({ apiKey }: { apiKey: ApiKeyResponse }) {
  return apiKeyStatus(apiKey) === 'active' ? (
    <Badge>Active</Badge>
  ) : (
    <Badge variant="destructive">Revoked</Badge>
  );
}

type SortField = 'name' | 'created' | 'lastUsed' | 'status';

function KeysTable({
  keys,
  onRevoke,
}: {
  keys: ApiKeyResponse[];
  onRevoke: (apiKey: ApiKeyResponse) => void;
}) {
  const {
    sorted,
    sortKey,
    direction,
    toggle: handleSort,
  } = useSort<ApiKeyResponse, SortField>(keys, {
    accessors: {
      name: (k) => k.name,
      created: (k) => new Date(k.created_at),
      lastUsed: (k) => (k.last_used_at ? new Date(k.last_used_at) : null),
      status: (k) => (apiKeyStatus(k) === 'active' ? 1 : 0),
    },
    // The backend lists newest first (created_at desc).
    initial: { key: 'created', direction: 'desc' },
    // Active maps to 1, so descending puts Active keys first on the first click.
    defaultDirections: { status: 'desc' },
  });

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <SortableTableHead sortKey="name" activeKey={sortKey} direction={direction} onSort={handleSort}>
            Name
          </SortableTableHead>
          <TableHead>Key</TableHead>
          <TableHead>Type</TableHead>
          <SortableTableHead sortKey="created" activeKey={sortKey} direction={direction} onSort={handleSort}>
            Created
          </SortableTableHead>
          <SortableTableHead sortKey="lastUsed" activeKey={sortKey} direction={direction} onSort={handleSort}>
            Last used
          </SortableTableHead>
          <SortableTableHead sortKey="status" activeKey={sortKey} direction={direction} onSort={handleSort}>
            Status
          </SortableTableHead>
          <TableHead className="text-right">
            <span className="sr-only">Actions</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sorted.map((apiKey) => {
          const active = apiKeyStatus(apiKey) === 'active';
          return (
            <TableRow key={apiKey.id} className={active ? undefined : 'text-muted-foreground'}>
              <TableCell className="w-full max-w-0">
                <div className="truncate font-medium" title={apiKey.name}>
                  {apiKey.name}
                </div>
              </TableCell>
              <TableCell>
                <code className="font-mono text-xs">{apiKey.masked_key}</code>
              </TableCell>
              <TableCell>
                <div className="flex gap-1">
                  <Badge variant={apiKey.environment === 'live' ? 'secondary' : 'outline'}>
                    {apiKey.environment === 'live' ? 'Live' : 'Test'}
                  </Badge>
                  <Badge variant="outline">
                    {accessForScopes(apiKey.scopes) === 'read_write' ? 'Read & write' : 'Read only'}
                  </Badge>
                </div>
              </TableCell>
              <TableCell className="whitespace-nowrap text-sm">{formatDate(apiKey.created_at, '—')}</TableCell>
              <TableCell className="whitespace-nowrap text-sm">
                {formatDateTime(apiKey.last_used_at, 'Never')}
              </TableCell>
              <TableCell>
                <StatusBadge apiKey={apiKey} />
              </TableCell>
              <TableCell className="text-right">
                {active && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                    aria-label={`Revoke ${apiKey.name}`}
                    onClick={() => onRevoke(apiKey)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function AdminOnlyNote() {
  return (
    <Alert>
      <Lock aria-hidden="true" />
      <AlertTitle>Only workspace admins can manage API keys</AlertTitle>
      <AlertDescription>
        <p>Ask an admin or the owner of this workspace to create a key for you.</p>
      </AlertDescription>
    </Alert>
  );
}

function ManageKeys() {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<ApiKeyResponse | null>(null);

  const { data: keys, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['api-keys'],
    queryFn: () => apiKeysApi.list(),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => apiKeysApi.revoke(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      setRevokeTarget(null);
      toast.success('API key revoked. It no longer works.');
    },
    onError: (error) => {
      toast.error(apiErrorMessage(error, 'The API key could not be revoked. Try again.'));
    },
  });

  // The client-side role said admin but the backend disagrees (role changed
  // mid-session, or a stale tenants cache): say why instead of blaming the network.
  if (isError && isForbiddenError(error)) {
    return (
      <>
        <KeysHeader />
        <AdminOnlyNote />
      </>
    );
  }

  const atLimit = !canCreateAnotherKey(keys);
  const active = activeKeyCount(keys);

  return (
    <>
      <KeysHeader
        badge={
          keys ? (
            <Badge variant={atLimit ? 'secondary' : 'outline'}>
              {active} of {MAX_ACTIVE_API_KEYS} active keys
            </Badge>
          ) : undefined
        }
        actions={
          <Button onClick={() => setCreateOpen(true)} disabled={isLoading || isError || atLimit}>
            <Plus aria-hidden="true" />
            Create API key
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Workspace keys</CardTitle>
          <CardDescription>
            Links and domains created in the dashboard aren&apos;t visible to the API.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {atLimit && keys && (
            <p className="text-sm text-muted-foreground">
              A workspace can have at most {MAX_ACTIVE_API_KEYS} active keys. Revoke one to create
              another; keeping two lets you rotate a key without downtime.
            </p>
          )}
          {isLoading ? (
            <div className="space-y-3" role="status" aria-label="Loading API keys">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : isError ? (
            <div className="flex flex-col items-start gap-3 py-8">
              <p className="font-medium">API keys could not be loaded</p>
              <p className="text-sm text-muted-foreground">
                {apiErrorMessage(error, 'Check your connection and try again.')}
              </p>
              <Button variant="outline" onClick={() => refetch()}>
                Try again
              </Button>
            </div>
          ) : keys && keys.length > 0 ? (
            <KeysTable keys={keys} onRevoke={setRevokeTarget} />
          ) : (
            <div className="flex flex-col items-center py-12 text-center">
              <div className="mb-4 flex size-12 items-center justify-center rounded-full bg-muted">
                <KeyRound className="size-6 text-muted-foreground" aria-hidden="true" />
              </div>
              <p className="font-medium">No API keys yet</p>
              <p className="mt-1 max-w-md text-sm text-muted-foreground">
                Create a key to connect a script, server or no-code tool. Up to {MAX_ACTIVE_API_KEYS} can
                be active at once.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <CreateApiKeyDialog open={createOpen} onOpenChange={setCreateOpen} />

      <AlertDialog
        open={revokeTarget !== null}
        onOpenChange={(open) => !open && !revoke.isPending && setRevokeTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke {revokeTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              The key stops working immediately. Any integrations using it will start failing with
              401 errors until you give them a new key. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revoke.isPending}>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={revoke.isPending}
              onClick={() => revokeTarget && revoke.mutate(revokeTarget.id)}
            >
              {revoke.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Revoking…
                </>
              ) : (
                'Revoke key'
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/**
 * Workspace API-key management. The backend requires the admin role for every
 * `/api-keys` call, so lower roles get an explanation instead of a list that
 * would only 403 — and the list query is never issued for them.
 */
export function ApiKeysPanel() {
  const { can, isLoading } = usePermissions();
  const isAdmin = can('admin');

  return (
    <div className="space-y-6">
      {isLoading ? (
        <>
          <KeysHeader />
          <div role="status" aria-label="Checking your permissions">
            <Skeleton className="h-40 w-full" />
          </div>
        </>
      ) : isAdmin ? (
        <ManageKeys />
      ) : (
        <>
          <KeysHeader />
          <AdminOnlyNote />
        </>
      )}
      <UsageCard />
    </div>
  );
}
